import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "sonner";

import { atualizarForma, criarForma, gravarTaxa, listarFormas, listarTaxas, mensagemDeErro } from "../api";
import { FORM_VAZIO, formDaForma, payloadDaForma, taxaDoServidor, taxaParaServidor } from "../lib/formulario";

function reducer(estado, acao) {
  if (acao.tipo === "carregar") return acao.form;
  if (acao.tipo === "campo") return { ...estado, [acao.campo]: acao.valor };
  if (acao.tipo === "sub") return { ...estado, [acao.campo]: { ...estado[acao.campo], [acao.chave]: acao.valor } };
  return estado;
}

/**
 * Estado da tela de edição. Três modos:
 *   id            → forma cadastrada (dados + taxa)
 *   chaveCanal    → canal sem cadastro (iFood, PIX/Cartão Online): só a taxa
 *   nenhum        → forma nova
 */
export default function useEditarForma({ id, chaveCanal }) {
  const [form, despachar] = useReducer(reducer, FORM_VAZIO);
  const [inicial, setInicial] = useState(FORM_VAZIO);
  const [info, setInfo] = useState({ taxaAtual: null, canal: null });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [formas, taxas] = await Promise.all([id ? listarFormas() : [], listarTaxas()]);
        if (!vivo) return;
        const todas = [...taxas.formas, ...taxas.canais];
        let novo = FORM_VAZIO;
        if (id) {
          const pm = formas.find((f) => String(f.id) === String(id));
          if (!pm) throw new Error("Forma de pagamento não encontrada");
          const taxa = todas.find((t) => t.chave === pm.code) || null;
          novo = formDaForma(pm, taxa);
          setInfo({ taxaAtual: taxa, canal: null });
        } else if (chaveCanal) {
          const canal = taxas.canais.find((c) => c.chave === chaveCanal);
          if (!canal) throw new Error("Canal não encontrado");
          novo = { ...FORM_VAZIO, name: canal.nome, code: canal.chave, taxa: taxaDoServidor(canal) };
          setInfo({ taxaAtual: canal, canal });
        }
        despachar({ tipo: "carregar", form: novo });
        setInicial(novo);
      } catch (e) {
        toast.error(mensagemDeErro(e, e.message || "Não consegui carregar a forma de pagamento."));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [id, chaveCanal]);

  const definir = useCallback((campo, valor) => despachar({ tipo: "campo", campo, valor }), []);
  const definirSub = useCallback((campo, chave, valor) => despachar({ tipo: "sub", campo, chave, valor }), []);
  const alterado = useMemo(() => JSON.stringify(form) !== JSON.stringify(inicial), [form, inicial]);

  const salvar = async () => {
    if (!chaveCanal && !form.name.trim()) { toast.error("Dê um nome para a forma de pagamento."); return false; }
    setSalvando(true);
    try {
      let chave = chaveCanal || form.code;
      if (!chaveCanal) {
        const payload = payloadDaForma(form, !id);
        const salva = id ? await atualizarForma(id, payload) : await criarForma(payload);
        chave = salva?.code || payload.code || form.code;
      }
      const taxaMudou = JSON.stringify(taxaParaServidor(form.taxa)) !== JSON.stringify(taxaParaServidor(inicial.taxa));
      // Taxa intocada não grava: salvar só o nome não pode marcar "0% configurado"
      // e esconder o aviso de forma sem taxa.
      if (taxaMudou) {
        try {
          await gravarTaxa(chave, taxaParaServidor(form.taxa));
        } catch (e) {
          if (e?.response?.status !== 403) throw e;
          toast.warning("Forma salva, mas você não tem permissão para alterar a taxa. Peça ao dono da loja a permissão Financeiro.");
          return true;
        }
      }
      toast.success(id || chaveCanal ? "Alterações salvas" : "Forma de pagamento criada");
      return true;
    } catch (e) {
      toast.error(mensagemDeErro(e, "Não consegui salvar. Confira os campos e tente de novo."));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  return { form, definir, definirSub, alterado, salvar, carregando, salvando, info };
}
